/**
 * Reports feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * All hooks are thin wrappers over orval-generated client
 * (`@shared/api/generated/reports/reports`).
 * They preserve the old call-site signatures with enabled logic.
 *
 * For new report endpoints always extend this file, never `@api/hooks.ts`.
 */
import type {
  ReportsAutoreadParams,
  ReportsConsumptionParams,
  ReportsEventLogParams,
  ReportsGroupProfileParams,
  ReportsLastDataParams,
  ReportsMatrixParams,
  ReportsNetworkParams,
  ReportsNetworkTelemetryParams,
  ReportsPhaseDiagramParams,
  ReportsPowerQualityParams,
  ReportsProfileParams,
  ReportsTouParams,
} from "@shared/api/generated/model";
import {
  useReportsAutoread as useGenAutoread,
  useReportsConsumption as useGenConsumption,
  useReportsEventLog as useGenEventLog,
  useReportsEventTypes as useGenEventTypes,
  useReportsGroupProfile as useGenGroupProfile,
  useReportsLastData as useGenLastData,
  useReportsMatrix as useGenMatrix,
  useReportsNetwork as useGenNetwork,
  useReportsNetworkTelemetry as useGenNetworkTelemetry,
  useReportsPhaseDiagram as useGenPhaseDiagram,
  useReportsPowerQuality as useGenPowerQuality,
  useReportsProfile as useGenProfile,
  useReportsTou as useGenTou,
} from "@shared/api/generated/reports/reports";

export type {
  AggregatedProfilePoint,
  AutoreadRow,
  ConsumptionRow,
  EventLogEntry,
  EventType,
  GroupProfileMeta,
  GroupProfileResponse,
  LastDataRow,
  LastDataSections,
  MatrixCell,
  MatrixResponse,
  MatrixRow,
  NetworkDataResponse,
  NetworkRow,
  NetworkTelemetryDataResponse,
  NetworkTelemetryMeta,
  NetworkTelemetryRow,
  PhaseAngles,
  PhaseDiagramPoint,
  PhaseDiagramResponse,
  PhaseDiagramVector,
  PhaseLineVoltages,
  PhaseTotals,
  PhaseVector,
  PowerQualityResponse,
  PowerQualityRow,
  PowerQualitySummary,
  ProfileRow,
  ReportsConsumptionParams,
  ReportsMatrixParams,
  ReportsNetworkTelemetryParams,
  ReportsPhaseDiagramParams,
  ReportsPowerQualityParams,
  TouRow,
} from "@shared/api/generated/model";

// ── Queries ─────────────────────────────────────────────────────────────────────

export const useLastData = (region?: number, object?: number, meter?: string, limit = 500) => {
  const enabled = region !== undefined && object !== undefined;
  const meterTrimmed = meter?.trim() || undefined;
  const params: ReportsLastDataParams = {
    region: region ?? 0,
    object: object ?? 0,
    ...(meterTrimmed && { meter: meterTrimmed }),
    limit,
  };
  return useGenLastData(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export const useEventTypes = () => useGenEventTypes({ query: { staleTime: 60_000 } });

export const useEventLog = (
  region?: number,
  object?: number,
  events?: number[],
  meter?: string,
  limit = 500,
) => {
  const enabled = region !== undefined && object !== undefined;
  const meterTrimmed = meter?.trim() || undefined;
  const params: ReportsEventLogParams = {
    region: region ?? 0,
    object: object ?? 0,
    ...(meterTrimmed && { meter: meterTrimmed }),
    ...(events && events.length > 0 && { events: events.join(",") }),
    limit,
  };
  return useGenEventLog(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface ConsumptionParams {
  region?: number;
  object?: number;
  from?: string;
  to?: string;
  measurement?: number;
  calc?: string;
  offsetMinutes?: number;
  roundDigits?: number;
  roundMode?: string;
  useBypass?: boolean;
}

export const useConsumption = (p: ConsumptionParams) => {
  const enabled =
    p.region !== undefined && p.object !== undefined && p.from !== undefined && p.to !== undefined;
  const params: ReportsConsumptionParams = {
    region: p.region ?? 0,
    object: p.object ?? 0,
    from: p.from ?? "",
    to: p.to ?? "",
    ...(p.measurement !== undefined && { measurement: p.measurement }),
    ...(p.offsetMinutes !== undefined && { offset: p.offsetMinutes }),
    ...(p.calc && { calc: p.calc }),
    ...(p.roundDigits !== undefined && { round_digits: p.roundDigits }),
    ...(p.roundMode && { round_mode: p.roundMode }),
    ...(p.useBypass && { use_bypass: p.useBypass }),
  };
  return useGenConsumption(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface AutoreadParams {
  region?: number;
  object?: number;
  date?: string;
  measurement?: number;
}

export const useAutoread = ({ region, object, date, measurement }: AutoreadParams) => {
  const enabled = region !== undefined && object !== undefined && date !== undefined;
  const params: ReportsAutoreadParams = {
    region: region ?? 0,
    object: object ?? 0,
    date: date ?? "",
    ...(measurement !== undefined && { measurement }),
  };
  return useGenAutoread(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface TouParams {
  region?: number;
  object?: number;
  measurement?: number;
  limit?: number;
}

export const useTou = ({ region, object, measurement, limit }: TouParams) => {
  const enabled = region !== undefined && object !== undefined;
  const params: ReportsTouParams = {
    region: region ?? 0,
    object: object ?? 0,
    ...(measurement !== undefined && { measurement }),
    ...(limit !== undefined && { limit }),
  };
  return useGenTou(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface ProfileParams {
  region?: number;
  object?: number;
  channel?: number;
  from?: string;
  to?: string;
  measurement?: number;
  step?: number;
  mode?: string;
}

export const useProfileData = ({
  region,
  object,
  channel,
  from,
  to,
  measurement,
  step,
  mode,
}: ProfileParams) => {
  const enabled = Boolean(region && object && channel && from && to);
  const params: ReportsProfileParams = {
    region: region ?? 0,
    object: object ?? 0,
    channel: channel ?? 0,
    ...(from && { from }),
    ...(to && { to }),
    ...(measurement !== undefined && { measurement }),
    ...(step !== undefined && { step }),
    ...(mode && { mode }),
  };
  return useGenProfile(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface ProfileGroupParams {
  groupID?: string;
  from?: string;
  to?: string;
  measurement?: number;
  step?: number;
  mode?: string;
}

export const useProfileGroupData = ({
  groupID,
  from,
  to,
  measurement = 1,
  step = 30,
  mode = "energy",
}: ProfileGroupParams) => {
  const enabled = Boolean(groupID && from && to);
  const params: ReportsGroupProfileParams = {
    from: from ?? "",
    to: to ?? "",
    measurement,
    step,
    mode,
  };
  return useGenGroupProfile(groupID ?? "", params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface NetworkParams {
  region?: number;
  object?: number;
  channel?: number;
  from?: string;
  to?: string;
  parameter?: string;
  useProfile?: boolean;
}

export const useNetworkData = ({
  region,
  object,
  channel,
  from,
  to,
  parameter,
  useProfile,
}: NetworkParams) => {
  const enabled = Boolean(region && object && channel && from && to);
  const params: ReportsNetworkParams = {
    region: region ?? 0,
    object: object ?? 0,
    channel: channel ?? 0,
    ...(from && { from }),
    ...(to && { to }),
    ...(parameter && { parameter }),
    ...(useProfile && { use_profile: true }),
  };
  return useGenNetwork(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface NetworkTelemetryParams {
  scope?: string;
  region?: number;
  object?: number;
  channel?: number;
  groupId?: string;
  from?: string;
  to?: string;
  interval?: number;
}

export const useNetworkTelemetry = ({
  scope,
  region,
  object,
  channel,
  groupId,
  from,
  to,
  interval,
}: NetworkTelemetryParams) => {
  const resolvedScope = scope?.trim() || (groupId ? "group" : channel ? "channel" : "object");
  const enabled =
    Boolean(from && to) &&
    (resolvedScope === "group"
      ? Boolean(groupId)
      : resolvedScope === "channel"
        ? Boolean(region && object && channel)
        : Boolean(region && object));

  const params: ReportsNetworkTelemetryParams = {
    scope: resolvedScope,
    ...(region !== undefined && { region }),
    ...(object !== undefined && { object }),
    ...(channel !== undefined && { channel }),
    ...(groupId && { group_id: groupId }),
    ...(from && { from }),
    ...(to && { to }),
    ...(interval && { interval }),
  };
  return useGenNetworkTelemetry(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface MatrixParams {
  region?: number;
  object?: number;
  channel?: number;
  date?: string;
  measurement?: number;
  step?: number;
  mode?: string;
  useLosses?: boolean;
  useMWT?: boolean;
}

export const useMatrixReport = ({
  region,
  object,
  channel,
  date,
  measurement,
  step,
  mode,
  useLosses,
  useMWT,
}: MatrixParams) => {
  const enabled = Boolean(region && object && channel && date);
  const params: ReportsMatrixParams = {
    region: region ?? 0,
    object: object ?? 0,
    channel: channel ?? 0,
    date: date ?? "",
    ...(measurement !== undefined && { measurement }),
    ...(step !== undefined && { step }),
    ...(mode && { mode }),
    ...(useLosses && { use_losses: true }),
    ...(useMWT && { use_mwt: true }),
  };
  return useGenMatrix(params, {
    query: { enabled, staleTime: 60_000 },
  });
};

export interface PhaseDiagramParams {
  region?: number;
  object?: number;
  channel?: number;
  date?: string;
  timestamp?: string;
  useKtn?: boolean;
  useLine?: boolean;
}

export const usePhaseDiagram = ({
  region,
  object,
  channel,
  date,
  timestamp,
  useKtn,
  useLine,
}: PhaseDiagramParams) => {
  const enabled =
    region !== undefined && object !== undefined && channel !== undefined && date !== undefined;
  const params: ReportsPhaseDiagramParams = {
    region: region ?? 0,
    object: object ?? 0,
    channel: channel ?? 0,
    date: date ?? "",
    ...(timestamp && { timestamp }),
    ...(useKtn && { use_ktn: true }),
    ...(useLine && { use_line: true }),
  };
  return useGenPhaseDiagram(params, {
    query: {
      enabled,
      staleTime: 60_000,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  });
};

export interface PowerQualityParams {
  region?: number;
  object?: number;
  channel?: number;
  date?: string;
  measurement?: number;
  step?: number;
  useRegisters?: boolean;
  useMWT?: boolean;
}

export const usePowerQuality = ({
  region,
  object,
  channel,
  date,
  measurement,
  step,
  useRegisters,
  useMWT,
}: PowerQualityParams) => {
  const enabled = Boolean(region && object && channel && date);
  const params: ReportsPowerQualityParams = {
    region: region ?? 0,
    object: object ?? 0,
    channel: channel ?? 0,
    date: date ?? "",
    ...(measurement !== undefined && { measurement }),
    ...(step !== undefined && { step }),
    ...(useRegisters && { use_registers: true }),
    ...(useMWT && { use_mwt: true }),
  };
  return useGenPowerQuality(params, {
    query: { enabled, staleTime: 60_000 },
  });
};
